'use client';

import { WalletName, WalletReadyState } from '@solana/wallet-adapter-base';
import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight } from '@drift-labs/icons';
import useWalletsToDisplay, {
	WalletToDisplayInfo,
} from 'src/hooks/useWalletsToDisplay';
import { DriftWalletName } from 'src/constants/wallets';
import { cm } from 'src/utils/classNames';
import { openUrl } from 'src/utils/browser';
import MarketIcon from '../Utils/MarketIcon';
import {
	WALLET_PROVIDER_BG_CLASSNAME,
	WalletButton,
	walletsToAddPaddingFor,
	WALLET_BUTTON_CLASSNAME,
} from './WalletButton';
import { EmailWalletsList } from './EmailWalletsList';

export const MoreWalletsCta = (props: {
	onClick: () => void;
	wallets: WalletToDisplayInfo[];
}) => {
	return (
		<button
			className="group flex w-full items-center justify-between"
			onClick={props.onClick}
		>
			<span className="typo-b3 text-text-default">More wallets</span>
			<div className="flex items-center">
				{props.wallets.slice(0, 3).map((wallet, index) => {
					const walletProviderBgClassName = (() => {
						const { name } = wallet.provider.adapter;
						if (!(name in WALLET_PROVIDER_BG_CLASSNAME)) {
							return;
						}

						return WALLET_PROVIDER_BG_CLASSNAME[name];
					})();

					return (
						<div
							className={cm(
								'-ml-sm flex size-5 items-center justify-center overflow-hidden rounded-full',
								walletsToAddPaddingFor.includes(wallet.provider.adapter.name) &&
									'p-xs',
								walletProviderBgClassName
							)}
							key={`${wallet.provider.adapter.name}-${index}`}
						>
							<img
								src={wallet.provider.adapter.icon}
								className={
									wallet.provider.adapter.name === 'Backpack'
										? 'size-[70%]'
										: 'size-full'
								}
							/>
						</div>
					);
				})}
				<div className="ml-3 flex items-center text-text-secondary group-hover:text-text-default">
					<ChevronRight size={20} />
				</div>
			</div>
		</button>
	);
};

const OtherWalletsDropdown = (props: {
	wallets: WalletToDisplayInfo[];
	onChooseWallet?: (selection: string) => void;
}) => {
	const contentDivRef = useRef<HTMLDivElement>(null);
	const [expanded, setExpanded] = useState(false);
	const [maxHeight, setMaxHeight] = useState(0);

	const toggleExpanded = () => {
		setExpanded(!expanded);
	};

	useEffect(() => {
		if (contentDivRef?.current?.offsetHeight) {
			setMaxHeight(contentDivRef.current?.offsetHeight);
		}
	}, [contentDivRef.current?.offsetHeight]);

	return (
		<div className="w-full">
			<MoreWalletsCta onClick={toggleExpanded} wallets={props.wallets} />

			<div
				className="w-full overflow-hidden transition-[max-height] duration-300"
				style={{ maxHeight: expanded ? `${maxHeight}px` : '0px' }}
			>
				<div ref={contentDivRef} className="flex flex-col gap-y-md pt-4">
					<EmailWalletsList
						onChooseWallet={(walletName: DriftWalletName) =>
							props.onChooseWallet?.(walletName as string)
						}
					/>
					{props.wallets.map((walletProvider, index) => (
						<WalletButton
							onChooseWallet={() =>
								props.onChooseWallet?.(
									walletProvider.provider.adapter.name ?? ''
								)
							}
							wallet={walletProvider}
							key={(walletProvider.provider.adapter.name ?? '') + index}
						/>
					))}
				</div>
			</div>
		</div>
	);
};

const ConnectWalletButtonsList = (props: {
	showUndetectedWallets?: boolean;
	numberOfWalletsToShow?: number;
	onSelectedWallet?: (walletName: DriftWalletName) => void;
}) => {
	const { walletsToShow, hiddenWallets } = useWalletsToDisplay(
		props.numberOfWalletsToShow
	);

	const handleSelectWallet = (name: DriftWalletName) => {
		props.onSelectedWallet?.(name);
	};

	const hasNoInstalledWallet =
		walletsToShow.length === 0 ||
		!walletsToShow.find(
			(wallet) => wallet.provider.readyState === WalletReadyState.Installed
		);

	return (
		<>
			<div className="mb-4 typo-b3">Select a wallet</div>

			{hasNoInstalledWallet && (
				<>
					<button
						onClick={() =>
							openUrl('https://www.drift.trade/learn/best-solana-wallets')
						}
						className={WALLET_BUTTON_CLASSNAME}
					>
						<div className="flex items-center">
							<div className="mr-4 size-6">
								<MarketIcon marketSymbol="SOL" className="size-full" />
							</div>
							<div className="text-left text-md text-text-default">
								Get a Solana Wallet
							</div>
						</div>
					</button>
				</>
			)}

			{/* Show regular Wallets to Show */}
			{walletsToShow.map((walletProvider) => (
				<WalletButton
					wallet={walletProvider}
					onChooseWallet={() =>
						handleSelectWallet(walletProvider.provider.adapter.name)
					}
					key={walletProvider.provider.adapter.name}
				/>
			))}

			{!props.showUndetectedWallets && hiddenWallets.length > 0 && (
				<OtherWalletsDropdown
					wallets={hiddenWallets}
					onChooseWallet={(walletName) =>
						handleSelectWallet(walletName as WalletName)
					}
				/>
			)}
		</>
	);
};

export default React.memo(ConnectWalletButtonsList);
